/**
 * The title theme (04 §5): an original tune, G major, 140 BPM, 32 bars that loop at 54.9
 * seconds. Theme - answer - theme - climb.
 *
 * The hook is the theme's first bar: four sixteenths rocketing up the triad to a held top note,
 * which then bounces down the chord in eighths. The same shape is fired off on G, D and E minor
 * before a 3 + 3 + 2 figure steps down to the dominant. The answer relaxes onto the subdominant
 * in long notes with a two-note tail, and lifts through A7 - the one chord from outside the key -
 * to D7. The climb is three hammered notes and a leap, in thirds with the second voice, twice,
 * each time higher, and a D7 arpeggio that runs straight back into the rocket.
 */

import { BOUNCE, HOP, OFFBEAT, TRESILLO } from './grooves';
import type { Song } from './types';

export const TITLE: Song = {
  bpm: 140,
  loop: true,
  bassFloor: 40,
  arpFloor: 60,
  mix: { lead: 0.24, alto: 0.07, arp: 0.11, bass: 0.26, drum: 0.24 },
  order: ['theme', 'answer', 'theme', 'climb'],
  patterns: {
    theme: {
      chords: 'G | D | Em | C D | G | D | C | D',
      lead: `
        G5 B5 D6 G6 . . D6 . B5 . D6 . G5 . . . |
        F#5 A5 D6 F#6 . . D6 . A5 . D6 . F#5 . . . |
        E5 G5 B5 E6 . . B5 . G5 . B5 . E5 . G5 . |
        C6 . . B5 . . A5 . D6 . . C6 . . A5 . |
        G5 B5 D6 G6 . . D6 . B5 . D6 . G6 . . . |
        F#5 A5 D6 F#6 . . D6 . A5 . D6 . A6 . . . |
        G6 . E6 . C6 . E6 . G6 . . A6 . . G6 . |
        D6 . . . . . A5 B5 C6 . B5 . A5 . F#5 .`,
      bass: { groove: BOUNCE },
      arp: TRESILLO,
    },
    answer: {
      chords: 'C | G | Am | D | C | G | A7 | D7',
      lead: `
        E6 . . . . . D6 C6 . . G5 . . . C6 . |
        B5 . . . . . A5 G5 . . D5 . . . G5 . |
        A5 . . . C6 . . . E6 . . . C6 . A5 . |
        F#5 . A5 . D6 . . . - . D5 E5 F#5 . A5 . |
        E6 . . . . . D6 C6 . . G5 . . . C6 . |
        D6 . . . . . B5 G5 . . D5 . . . G5 . |
        A5 . C#6 . E6 . G6 . . . E6 . C#6 . A5 . |
        D6 . . . C6 . A5 . F#5 . A5 . C6 . D6 .`,
      bass: { groove: HOP },
    },
    climb: {
      chords: 'Em | C | G | D | Em | C | D | D7',
      lead: `
        B5 . . B5 . . B5 . E6 . . . B5 . . . |
        C6 . . C6 . . C6 . E6 . . . G6 . . . |
        D6 G6 D6 B5 D6 B5 G5 B5 G5 . . . D5 . . . |
        A5 . . . F#5 . A5 . D6 . . . . . - . |
        B5 . . B5 . . B5 . E6 . . . G6 . . . |
        E6 . . E6 . . E6 . G6 . . . C7 . . . |
        A6 . F#6 . D6 . A5 . F#6 . D6 . A5 . F#5 . |
        D5 F#5 A5 C6 D6 . . . - . . . D6 . . .`,
      alto: `
        G5 . . G5 . . G5 . B5 . . . G5 . . . |
        G5 . . G5 . . G5 . C6 . . . E6 . . . |
        - . . . . . . . . . . . . . . . |
        - . . . . . . . . . . . . . . . |
        G5 . . G5 . . G5 . B5 . . . E6 . . . |
        C6 . . C6 . . C6 . E6 . . . G6 . . . |
        - . . . . . . . . . . . . . . . |
        - . . . . . . . . . . . . . . .`,
      bass: { groove: BOUNCE },
      arp: OFFBEAT,
    },
  },
};
